import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { isNil } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useRecoilState, useSetRecoilState } from 'recoil';

import MultiSelect from 'components/MultiSelect';

import { warehouseState } from 'atoms/WarehouseState';
import { warehouseFilterState, regionListState } from 'atoms/WarehouseFilterState';

import * as Constants from '../constants';
import useSearchParams from 'hooks/useSearchParams';
import { listExistClient, listWarehouseRegion, listWarehouse } from 'repositories/HttpRepository';

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    alignItems: 'flex-end',
    marginTop: 30,
  },
  simulationCheckbox: {
    marginLeft: 16,
  },
}));

function Filter({ dspList }) {
  const { classes } = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();

  const setWarehouses = useSetRecoilState(warehouseState);

  const defaultTypeOptions = [
    { label: 'Main', value: Constants.TYPE_MAIN },
    { label: 'Mobile Hub', value: Constants.TYPE_MOBILE_HUB },
  ];

  const selectedRegions = searchParams.get('regions');
  const selectedRegionOptions = selectedRegions
    ? selectedRegions.split(',').map((region) => ({ label: region, value: region }))
    : [];

  const dspOptions = useMemo(
    () =>
      dspList?.items?.map((dsp) => ({
        label: dsp.code ?? dsp.company,
        value: dsp.id,
      })),
    [dspList],
  );

  const [regions, setRegions] = useRecoilState(regionListState);
  const [filterState, setFilterState] = useRecoilState(warehouseFilterState);

  const [selectedOwnTypes, setSelectedOwnTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [includeSimulation, setIncludeSimulation] = useState(false);

  function listRegions(params, callback) {
    listWarehouseRegion(params).then((resp) => {
      if (resp.ok) {
        const regions = resp.data.items.map((region) => ({
          label: region,
          value: region,
        }));

        callback(regions);
        return;
      }
    });
  }

  useEffect(() => {
    listRegions({}, (regions) => {
      setRegions(regions);
    });
  }, []);

  function handleOnChangeOwnerType(selectedOwnerType) {
    setSelectedOwnTypes(selectedOwnerType);
    if (selectedOwnerType.length !== 1) {
      listRegions({}, (regions) => {
        setRegions(regions);
        setFilterState((previous) => ({
          ...previous,
          selected: { ...previous.selected, owner_types: null, page: null },
        }));
      });
      return;
    }

    if (selectedOwnerType[0].value === Constants.OWNER_TYPE_JITSU) {
      listRegions({ owner_type: Constants.OWNER_TYPE_MAIN }, (regions) => {
        setRegions(regions);
        setFilterState((previous) => ({
          ...previous,
          selected: {
            ...previous.selected,
            owner_types: [Constants.OWNER_TYPE_MAIN, Constants.OWNER_TYPE_MOBILE],
            page: null,
          },
          typeOptions: defaultTypeOptions,
        }));
        setSelectedTypes(
          defaultTypeOptions?.filter((client) =>
            selectedTypes.some((st) => st.value === client.value && st.label === client.label),
          ),
        );
      });
    } else if (selectedOwnerType[0].value === Constants.OWNER_TYPE_CLIENT) {
      listExistClient().then((resp) => {
        if (resp.ok) {
          const clients = resp.data.items.map((client) => ({
            label: client.name,
            value: client.id,
          }));

          listRegions({ owner_type: Constants.OWNER_TYPE_CLIENT_STR }, (regions) => {
            setRegions(regions);
            setFilterState((previous) => ({
              ...previous,
              typeOptions: clients,
              selected: {
                ...previous.selected,
                owner_types: [Constants.OWNER_TYPE_CLIENT_STR],
                page: null,
              },
            }));
            setSelectedTypes(
              clients?.filter((client) =>
                selectedTypes.some((st) => st.value === client.value && st.label === client.label),
              ),
            );
          });
        }
      });
    } else if (selectedOwnerType[0].value === Constants.OWNER_TYPE_COURIER) {
      listRegions({ owner_type: Constants.OWNER_TYPE_COURIER_STR }, (regions) => {
        setRegions(regions);
        setFilterState((previous) => ({
          ...previous,
          typeOptions: dspOptions,
          selected: {
            ...previous.selected,
            owner_types: [Constants.OWNER_TYPE_COURIER_STR],
            page: null,
          },
        }));
        setSelectedTypes([]);
      });
    }
  }

  function handleOnRemoveOwnerType() {
    listRegions(null, (regions) => {
      setRegions(regions);
      setFilterState((previous) => ({
        ...previous,
        selected: {
          ...previous.selected,
          owner_ids: null,
          owner_types: null,
          keyword: null,
          page: null,
        },
      }));
    });
    setSelectedOwnTypes([]);
  }

  function handleOnChangeRegion(selectedOptions) {
    const selectedRegions = selectedOptions.map((option) => option.value);
    searchParams.set('regions', selectedRegions.toString());
    setSearchParams(searchParams);
  }

  function handleOnRemoveRegion() {
    searchParams.delete('regions');
    setSearchParams(searchParams);
  }

  function handleOnChangeType(objs) {
    setSelectedTypes(objs);
    if (JSON.stringify(filterState.selected.owner_types) !== JSON.stringify([Constants.OWNER_TYPE_CLIENT_STR])) {
      const ownerTypes = objs?.map((type) => type.label);

      setFilterState((previous) => ({
        ...previous,
        selected: {
          ...previous.selected,
          owner_types: ownerTypes,
          page: null,
        },
      }));
    } else {
      // no-dd-sa:javascript-code-style/assignment-name
      const owner_ids = objs.map((item) => item.value);

      setFilterState((previous) => ({
        ...previous,
        selected: {
          ...previous.selected,
          owner_types: [Constants.OWNER_TYPE_CLIENT_STR],
          owner_ids,
          page: null,
        },
      }));
    }
  }

  function handleOnRemoveType() {
    if (JSON.stringify(filterState.selected.owner_types) !== JSON.stringify([Constants.OWNER_TYPE_CLIENT_STR])) {
      setFilterState({
        ...filterState,
        selected: {
          ...filterState.selected,
          owner_ids: null,
          owner_types: [Constants.OWNER_TYPE_MAIN, Constants.OWNER_TYPE_MOBILE],
        },
      });
    } else {
      setFilterState({
        ...filterState,
        selected: { ...filterState.selected, owner_ids: null },
      });
    }
    setSelectedTypes([]);
  }

  const handleRefreshWarehouseList = useCallback(() => {
    const regions = selectedRegions ? selectedRegions.split(',') : [];
    const params = {
      ...filterState.selected,
      regions: regions,
      order_by: filterState.sort.order_by,
      desc: filterState.sort.is_desc,
    };

    const payload = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (isNil(value)) continue;

      if (Array.isArray(value)) {
        value.forEach((val) => payload.append(key, val));
        continue;
      }
      payload.set(key, value);
    }

    if (includeSimulation) {
      payload.set('ignore_owner_types[]', '');
    }
    listWarehouse(payload).then((response) => {
      if (response.ok) setWarehouses(response.data);
    });
  }, [filterState, selectedRegions]);

  useEffect(() => {
    handleRefreshWarehouseList();
  }, [handleRefreshWarehouseList]);

  const handleIncludeSimulationChange = useCallback(
    (e) => {
      setIncludeSimulation(e.target.checked);
      setFilterState((previous) => ({
        ...previous,
        selected: { ...previous.selected, page: null },
      }));
    },
    [setFilterState],
  );

  return (
    <div className={classes.root}>
      <MultiSelect
        label="Overview for"
        options={Constants.defaultWarehouseTypeOptions}
        placeholder="all warehouses"
        onChange={handleOnChangeOwnerType}
        onRemove={handleOnRemoveOwnerType}
        selectedValues={selectedOwnTypes}
      />
      <MultiSelect
        label="at"
        options={regions}
        placeholder="all regions"
        onChange={handleOnChangeRegion}
        onRemove={handleOnRemoveRegion}
        selectedValues={selectedRegionOptions}
      />

      {JSON.stringify(filterState.selected.owner_types) !== JSON.stringify([Constants.OWNER_TYPE_COURIER_STR]) && (
        <MultiSelect
          label="for"
          options={filterState.typeOptions}
          placeholder="all types"
          onChange={handleOnChangeType}
          onRemove={handleOnRemoveType}
          selectedValues={selectedTypes}
        />
      )}

      <FormControlLabel
        className={classes.simulationCheckbox}
        control={
          <Checkbox
            color="primary"
            checked={includeSimulation}
            onChange={handleIncludeSimulationChange}
            sx={{ py: 0 }}
          />
        }
        label="Incl. Simulation"
      />
    </div>
  );
}

export default Filter;
